/**
 * Tenant content for user-modern — ใช้ Configuration Service แบบเดียวกับ user-classic
 */
export interface TenantContentResponse {
  success: boolean
  data: {
    tenantId: string
    name: string
    description?: string
    branding: { logo: string; favicon: string; [k: string]: unknown }
    content: {
      siteTitle?: string
      marqueeText?: string
      welcomeMessage?: string
      contactInfo?: { line?: string; phone?: string; [k: string]: unknown }
      socialLinks?: unknown
      paymentRates?: {
        type?: 'text' | 'image'
        textContent?: string
        imageUrl?: string
      }
    }
    features?: {
      webChatEnabled?: boolean
      depositEnabled?: boolean
      withdrawEnabled?: boolean
      lotteryEnabled?: boolean
      casinoEnabled?: boolean
      predictionEnabled?: boolean
      [k: string]: unknown
    }
  }
}

/**
 * Base URL สำหรับเรียก API — ให้ตรงกับ user-classic
 * ใช้ absolute URL ไปที่ load balancer/backend โดยตรง (ไม่ผ่าน Vite proxy)
 * VITE_API_BASE_URL || 'http://localhost:8081' เหมือนใน tenantService ของ user-classic
 */
function getBaseURL(): string {
  const base = import.meta.env.VITE_API_BASE_URL
  if (base !== undefined && base !== '') return base
  return 'http://localhost:8081'
}

function normalizeResponse(json: unknown, tenantId: string): TenantContentResponse {
  const data = (json as { data?: unknown }).data ?? json
  const d = data as Record<string, unknown>
  const content = (d.content ?? d) as Record<string, unknown>
  return {
    success: (json as { success?: boolean }).success !== false,
    data: {
      tenantId: (d.tenantId as string) ?? tenantId,
      name: (d.name as string) ?? 'HuayToday',
      description: d.description as string | undefined,
      branding: (d.branding as TenantContentResponse['data']['branding']) ?? { logo: '', favicon: '' },
      content: {
        siteTitle: (content.siteTitle as string) || undefined,
        marqueeText: (content.marqueeText as string) ?? (d.marqueeText as string),
        welcomeMessage: content.welcomeMessage as string | undefined,
        contactInfo: (content.contactInfo ?? d.contactInfo) as TenantContentResponse['data']['content']['contactInfo'],
        socialLinks: (content.socialLinks ?? d.socialLinks) as unknown,
        paymentRates: content.paymentRates as TenantContentResponse['data']['content']['paymentRates'],
      },
      features: d.features as TenantContentResponse['data']['features'],
    },
  }
}

export const getTenantContent = async (tenantId: string): Promise<TenantContentResponse> => {
  const baseURL = getBaseURL()
  const headers = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId,
    'X-Tenant-Domain': window.location.hostname,
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  }

  try {
    // 1) Configuration Service — endpoint เดียวกับแอดมิน (จัดการเนื้อหา Tenant ที่ /tenant/content)
    // tenantId มาจาก App.tsx → loadTenantTheme() → getTenantId() (URL ?tenant= > env VITE_* > default test-demo-01)
    const configUrl = `${baseURL}/api/v1/configuration-service/tenant-content/${tenantId}?_t=${Date.now()}`
    if (import.meta.env.DEV) {
      console.log('📢 [tenantService] GET tenant content (ที่เดียวกับแอดมิน):', {
        url: configUrl,
        tenantId,
      })
    }
    const configRes = await fetch(configUrl, { method: 'GET', headers })

    if (configRes.status === 304) {
      return getFallbackResponse(tenantId)
    }
    if (configRes.ok) {
      const json = await configRes.json()
      const out = normalizeResponse(json, tenantId)
      if (import.meta.env.DEV) {
        const raw = out.data.content.marqueeText ?? ''
        console.log('✅ [tenantService] Tenant content from Configuration Service', {
          tenantId: out.data.tenantId,
          marqueeLength: raw.length,
          marqueeFull: raw,
        })
      }
      return out
    }

    // 2) Fallback ไปที่ tenant-management แบบเดียวกับ user-classic
    if (import.meta.env.DEV) {
      console.warn('Configuration Service failed, trying tenant-management fallback')
    }
    const fallbackUrl = `${baseURL}/api/v1/tenant-management/tenants/${tenantId}/content`
    const fallbackRes = await fetch(fallbackUrl, { method: 'GET', headers })
    if (fallbackRes.ok) {
      const json = await fallbackRes.json()
      const out = normalizeResponse(json, tenantId)
      if (import.meta.env.DEV) {
        console.log('⚠️ Tenant content loaded from tenant-management fallback')
      }
      return out
    }

    throw new Error(`HTTP ${configRes.status}`)
  } catch (e) {
    console.warn('Failed to fetch tenant content:', e)
    return getFallbackResponse(tenantId)
  }
}

function getFallbackResponse(tenantId: string): TenantContentResponse {
  return {
    success: true,
    data: {
      tenantId,
      name: 'HuayToday',
      branding: { logo: '', favicon: '' },
      content: {
        marqueeText:
          'ยินดีต้อนรับทุกท่านเข้าสู่เว็บ lenhuay.net เว็บหวยออนไลน์ที่มาแรงที่สุดในตอนนี้ มั่นคง ซื่อตรง รับประกันจ่ายจริงทุกโพย',
      },
    },
  }
}
